import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { Autocomplete, createFilterOptions, TextField } from '@mui/material';
import { useFindInventoriesForSelectionLazy } from '@src/adapters/hooks/inventory/useFindInventoryForSelection';
import InventorySelectionItemDTO from '@src/adapters/dto/inventory/InventorySelectionItemDTO';
import { debounce } from 'lodash';
import InventoryAdd from './InventoryAdd';
import InventoryDTO from '@src/adapters/dto/inventory/InventoryDTO';
import useCachedCategoriesList from '@src/adapters/hooks/category/useCachedCategoriesList';
import IdNameDto from '@src/adapters/dto/shared/IdNameDto';
import DoneIcon from '@mui/icons-material/Done';

interface Props {
  name: string;
  control: Control<any>;
  label: string;
  search?: string;
  exclude?: string[];
  allowAdd?: boolean;
  setValue?: UseFormSetValue<any>;
}

const filter = createFilterOptions<InventorySelectionItemDTO>();

function InventoryAutoComplete({
  name,
  control,
  label,
  search,
  exclude,
  allowAdd = false,
  setValue,
}: Props) {
  const [items, setItems] = useState<InventorySelectionItemDTO[]>([]);
  const [categories, setCategories] = useState<IdNameDto[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [matched, setMatched] = useState(false);
  const [open, setOpen] = useState(false);
  const { execute: findInvs } = useFindInventoriesForSelectionLazy();
  const value = useWatch({ control, name });

  const [addOpen, setAddOpen] = useState(false);
  const { getList } = useCachedCategoriesList();

  useEffect(() => {
    getList().then((result) => {
      setCategories(result);
    });
  }, [getList]);

  useEffect(() => {
    if (search) {
      setIsLoading(true);
      setSearchTerm(search);
    }
  }, [search]);

  const invRef = useRef(null);

  const debouncedFetchInventories = useMemo(
    () =>
      debounce(async (query: string) => {
        if (query.length < 2) {
          return;
        }
        const result = await findInvs({ filter: query });
        const filteredItems = exclude
          ? result.inventories.filter((i) => !exclude.includes(i.id!))
          : result.inventories;

        setItems(filteredItems);
        if (isLoading) {
          if (
            filteredItems.length === 1 &&
            search?.toLocaleLowerCase() ===
              filteredItems[0].name.toLocaleLowerCase()
          ) {
            setInputValue(filteredItems[0].name);
            if (setValue) {
              setValue('inventory', {
                id: filteredItems[0].id!,
                name: filteredItems[0].name,
              });
            }

            setMatched(true);
          } else if (filteredItems.length > 1 && search) {
            setOpen(true);
          }
        }
        setIsLoading(false);
      }, 300),
    [exclude, findInvs, isLoading, search, setValue]
  );

  const fetchInventories = useCallback(
    (query: string) => debouncedFetchInventories(query),
    [debouncedFetchInventories]
  );

  useEffect(() => {
    fetchInventories(searchTerm);
  }, [searchTerm, fetchInventories]);

  const handleAddClosed = (inventory: InventoryDTO | undefined) => {
    if (inventory && inventory.id) {
      const category = categories.find((c) => c.id === inventory.categoryId!);
      const inventoryToAdd = {
        id: inventory.id,
        name: inventory.name,
        category: {
          id: inventory.categoryId,
          name: category ? category.name : '',
        },
      };
      setItems((prevItems) => [...prevItems, inventoryToAdd]);

      if (setValue) {
        setValue('inventory', {
          id: inventoryToAdd.id!,
          name: inventoryToAdd.name,
        });
      }
    }
    setAddOpen(false);
  };

  useEffect(() => {
    if (!search) {
      if (!value || !value.id) {
        setInputValue('');
        setItems([]);
        setSearchTerm('');
      }
    }
  }, [value, search]);

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange } }) => (
          <Autocomplete
            ref={invRef}
            options={items.sort(
              (a, b) => -b.category.name.localeCompare(a.category.name)
            )}
            open={open}
            freeSolo={allowAdd}
            clearOnBlur={allowAdd && !inputValue}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.name
            }
            inputValue={inputValue}
            value={
              items.find((opt) => opt.id === value) ||
              (typeof value === 'string'
                ? { id: value, name: value, category: { name: '' } }
                : null)
            }
            onClose={() => setOpen(false)}
            groupBy={(option) => option.category.name}
            onInputChange={(_, newInputValue, reason) => {
              setOpen(true);
              if (allowAdd && reason !== 'reset') {
                onChange(null);
              }
              setMatched(false);
              setInputValue(newInputValue);
              setSearchTerm(newInputValue);
            }}
            onChange={(_, newValue, reason) => {
              setMatched(false);
              const isObject = typeof newValue === 'object';

              if (!isObject) {
                setInputValue('');
                setAddOpen(true);
              } else if (!newValue) {
                onChange(null);
              } else if (newValue.id) {
                onChange(newValue);
                setInputValue(newValue.name);
              } else {
                setInputValue(newValue.newName!);
                setAddOpen(true);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {matched && <DoneIcon />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              const { inputValue } = params;

              const isExisting = options.some(
                (option) => inputValue === option.name
              );

              if (!isExisting && allowAdd) {
                filtered.push({
                  name: `Add ${inputValue ? inputValue : 'New Item'}`,
                  category: { name: 'New Item' },
                  newName: inputValue,
                });
              }

              return filtered;
            }}
          />
        )}
      />
      <InventoryAdd
        open={addOpen}
        close={handleAddClosed}
        itemName={inputValue}
        anchorEl={invRef}
      />
    </>
  );
}

export default InventoryAutoComplete;
